import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, AlertTriangle, CalendarDays, CreditCard, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useDemo } from "@/store/DemoStore";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Logo, btn } from "@/components/common";

const FEATURES = [[CalendarDays, "Day-by-day itinerary"], [CreditCard, "Invoices & payments"], [MessageCircle, "Chat with your advisor"]];

function ClientLogin() {
  const { refresh } = useDemo();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/client-login", f);
      await refresh();
      nav("/mytrip");
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };
  const input = "h-11 w-full rounded-xl border border-white/10 bg-white/10 px-4 text-base text-white outline-none placeholder:text-white/40 focus:border-white/40 sm:text-sm";
  return (
    <form onSubmit={submit} className="mt-6 space-y-2.5" data-testid="client-login-form">
      <input required type="email" autoComplete="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={input} data-testid="client-email-input" />
      <input required type="password" autoComplete="current-password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={input} data-testid="client-password-input" />
      <button disabled={busy} data-testid="client-login-submit" className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-white text-sm font-semibold text-navy-900 transition-colors hover:bg-slate-100 disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign In to MyTrip <ArrowRight className="h-4 w-4" /></>}
      </button>
    </form>
  );
}

function AgentLogin() {
  const { refresh, setNotice } = useDemo();
  const nav = useNavigate();
  const [f, setF] = useState({ username: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/login", f);
      await refresh();
      nav("/agent");
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.detail?.toLowerCase().includes("locked")) setNotice(err.response.data.detail);
      else toast.error(errMsg(err));
    }
    setBusy(false);
  };
  const input = "h-10 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 sm:text-sm";
  return (
    <div className="mt-8 border-t border-slate-200 pt-6" data-testid="agent-login-area">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Travel advisor sign in</p>
        <Logo size="h-4" />
      </div>
      <form onSubmit={submit} className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
        <input required autoComplete="username" placeholder="Username (e.g. cmorris)" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} className={input} data-testid="agent-username-input" />
        <input required type="password" autoComplete="current-password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={input} data-testid="agent-password-input" />
        <button disabled={busy} data-testid="agent-login-submit" className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg bg-navy-900 px-4 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign In <ArrowRight className="h-4 w-4" /></>}
        </button>
      </form>
    </div>
  );
}

export default function PortalLogin() {
  const { status, me, notice, clearNotice } = useDemo();
  const nav = useNavigate();
  useEffect(() => {
    if (status === "ready") nav(me.kind === "agent" ? "/agent" : "/mytrip", { replace: true });
    if (status === "mustChange") nav("/agent", { replace: true });
    if (status === "agreement") nav("/agent", { replace: true });
  }, [status, me, nav]);

  return (
    <div className="app-shell scroll-area flex flex-col bg-white" data-testid="portal-login-page">
      <Link to="/" data-testid="back-to-website-banner" className="hidden h-10 shrink-0 items-center justify-center gap-1.5 bg-navy-900 sm:flex text-xs font-medium text-white/90 transition-colors hover:bg-navy-800 sm:text-sm">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Topsail Travel website
      </Link>
      <div className="flex flex-1 flex-col lg:flex-row">
        <div className="relative h-56 shrink-0 overflow-hidden sm:h-72 lg:h-auto lg:w-[52%]">
          <img src="/images/santorini.jpg" alt="Santorini at sunset" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-950/70 via-navy-950/15 to-navy-950/85" />
          <div className="relative flex h-full flex-col justify-between p-6 sm:p-10">
            <Logo light brand="mytrip" size="h-14 sm:h-20" sub="Powered by Topsail Travel" />
            <p className="max-w-md font-display text-2xl font-bold leading-tight text-white sm:text-3xl lg:text-4xl">Your trip, all in one place.</p>
          </div>
        </div>
        <div className="flex flex-1 items-center justify-center px-5 py-10 sm:px-10">
          <div className="w-full max-w-md">
            <h1 className="font-display text-3xl font-bold tracking-tight text-navy-900 sm:text-4xl" data-testid="login-title">Welcome to MyTrip</h1>
            <p className="mb-7 mt-2 text-sm text-slate-500 sm:text-base">See your itinerary, payments and messages from your advisor.</p>
            <div className="rounded-3xl bg-navy-900 p-6 text-white shadow-[0_18px_40px_rgba(11,25,44,0.22)] sm:p-7" data-testid="client-login-card">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/50">Traveler sign in</p>
              <ul className="mt-5 hidden space-y-2.5 sm:block">
                {FEATURES.map(([Icon, l]) => <li key={l} className="flex items-center gap-3 text-sm text-white/80"><Icon className="h-4 w-4 text-sky-300" />{l}</li>)}
              </ul>
              <ClientLogin />
              <p className="mt-4 text-xs leading-relaxed text-white/50">New to MyTrip? Use the invite link your advisor emailed you to create your account.</p>
            </div>
            <AgentLogin />
          </div>
        </div>
      </div>
      <Dialog open={!!notice} onOpenChange={(o) => { if (!o) clearNotice(); }}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-sm rounded-2xl p-6 text-center sm:p-7" data-testid="account-locked-dialog">
          <DialogHeader>
            <div className="mx-auto mb-2 grid h-12 w-12 place-items-center rounded-full bg-red-50 text-red-600"><AlertTriangle className="h-6 w-6" /></div>
            <DialogTitle className="font-display text-xl text-navy-900">Account Locked</DialogTitle>
            <DialogDescription data-testid="account-locked-message">{notice}</DialogDescription>
          </DialogHeader>
          <button onClick={clearNotice} className={`${btn.primary} mt-2 w-full justify-center`} data-testid="account-locked-dismiss">Got It</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
